import './Main.scss';
import { useNavigate, Link } from 'react-router-dom';
import { useEffect } from 'react';
import series from '../../data/series.json';

const imgSrc = path => path.split('/').map(encodeURIComponent).join('/');
const allWorks = series.flatMap(s => s.works);
const featured = [29, 30, 5, 10].map(id => allWorks.find(w => w.id === id));

const heroBgImages = [
  '/images/series/Decode Me/4/main.jpg',
  '/images/series/Decode Me/3/main.jpg',
  '/images/series/Decode Me/2/main.jpg',
  '/images/series/Homo viator/Golden Today, oil on canvas, 100x70 cm, 2025/Golden Today_Anna Hovan_100_70_2025.jpg',
  '/images/series/Homo viator/The Call, oil on canvas, 100x70 cm, 2025/The Call_ Anna Hovan_100x70_2025.jpg',
];

function Main() {
  const navigate = useNavigate();

  useEffect(() => {
    heroBgImages.forEach(src => {
      const link = document.createElement('link');
      link.rel = 'preload';
      link.as = 'image';
      link.href = src;
      document.head.appendChild(link);
    });
  }, []);
  return (
    <>
      <div className='main'>
        <div className='hero-slides'>
          {heroBgImages.map((src, i) => (
            <div
              key={src}
              className='hero-slide'
              style={{ backgroundImage: `url('${src}')`, animationDelay: `${i * 5 - 0.75}s` }}
            />
          ))}
        </div>
        <div className='hero-overlay' />
        <div className='hero-content'>
          <span className='hero-eyebrow'>Contemporary Artist</span>
          <h1 className='hero-title'>Anna Hovan</h1>
          <button className='hero-btn' onClick={() => navigate('/works')}>Explore Works</button>
        </div>
      </div>

      <section id='featured' className='featured-section'>
        <div className='featured-header'>
          <span className='featured-eyebrow'>Selected Works</span>
          <h2 className='featured-title'>Featured Paintings</h2>
        </div>
        <div className='featured-grid'>
          {featured.map(work => (
            <Link key={work.id} to={`/works/${work.id}`} className='featured-card'>
              <div className='featured-card-img'>
                <img src={imgSrc(work.main)} alt={work.title} />
              </div>
              <div className='featured-card-info'>
                <span className='featured-card-title'>{work.title}</span>
                <span className='featured-card-desc'>{work.medium} · {work.dimensions}</span>
              </div>
            </Link>
          ))}
        </div>
        <div className='featured-footer'>
          <Link to='/works' className='featured-all-link'>View All Works →</Link>
        </div>
      </section>

      <section id='exhibitions' className='exhibitions-section'>
        <div className='exhibitions-inner'>
          <div className='exhibitions-header'>
            <span className='exhibitions-eyebrow'>Exhibitions & Fairs</span>
            <h2 className='exhibitions-title'>Group Exhibitions, Fairs</h2>
          </div>
          <ul className='exhibitions-list'>
            {[
              { year: '2026', title: 'Two-Artist Exhibition', detail: 'Armenian Artists Project & Mouradick Art Center, Yerevan, Armenia' },
              { year: '2026', title: 'SIROON ARM FEST', detail: 'Contemporary Art Exhibition & Sale, Yerevan, Armenia. Live Art Performance — part of the exhibition program: collaborative live painting with four contemporary Armenian artists, creating one artwork in real time before an audience' },
              { year: '2026', title: 'Featured Artwork', detail: 'Armenia Artist Project, Armenia Marriott Hotel, Yerevan, Armenia' },
              { year: '2026', title: '"The Fulcrum"', detail: 'Group Exhibition, Dilijan, Armenia' },
              { year: '2026', title: '"Resilient?"', detail: 'Group Exhibition & Sale, ArAr Gallery, Yerevan, Armenia' },
              { year: '2026', title: '"Maternity and Beauty"', detail: 'Group Exhibition, Yerevan State University, Yerevan, Armenia' },
              { year: '2025', title: 'World Art Dubai', detail: 'International Art Fair, Dubai, UAE' },
              { year: '2025', title: '"The Color of Peace"', detail: 'Charity Group Exhibition & Sale, Two Arcs Gallery, Yerevan, Armenia' },
              { year: '2024', title: '"The Color of Peace"', detail: 'Charity Group Exhibition & Sale, Yerevan Modern Art Museum, Yerevan, Armenia' },
              { year: '2024', title: 'Christmas Art Fair', detail: 'Artist Union of Armenia, Yerevan, Armenia' },
              { year: '2020', title: 'Group Exhibition', detail: 'K. Demirchyan Museum, Yerevan, Armenia' },
            ].map((ex, i) => (
              <li key={i} className='exhibitions-item'>
                <span className='exhibitions-year'>{ex.year}</span>
                <span className='exhibitions-name'>{ex.title}</span>
                <span className='exhibitions-detail'>{ex.detail}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className='press-section'>
        <div className='press-inner'>
          <div className='press-header'>
            <span className='press-eyebrow'>Press & Features</span>
            <h2 className='press-title'>In the Media</h2>
          </div>
          <div className='press-grid'>
            <a
              className='press-card'
              href='https://www.artmajeur.com/en/magazine/8-meet-and-discover/artist-interview-i-anna-hovan/340909'
              target='_blank'
              rel='noreferrer'
            >
              <span className='press-card-source'>Artmajeur Magazine</span>
              <span className='press-card-title'>Artist Interview — Anna Hovan</span>
              <span className='press-card-arrow'>→</span>
            </a>
            <a
              className='press-card'
              href='https://www.armmuseum.ru/news-blog/anna-hovan-beauty-is-the-moment-when-a-person-momentarily-coincides-with-their-own-truth'
              target='_blank'
              rel='noreferrer'
            >
              <span className='press-card-source'>Armenian Museum of Moscow</span>
              <span className='press-card-title'>Anna Hovan — "Beauty is the moment when a person momentarily coincides with their own truth"</span>
              <span className='press-card-arrow'>→</span>
            </a>
          </div>
        </div>
      </section>

      <section id='about' className='about-section'>
        <div className='about-inner'>
          <div className='about-left'>
            <span className='about-eyebrow'>About the Artist</span>
            <blockquote className='about-quote'>
              "To create is to say yes — to beauty, to love, to the endless search for light."
            </blockquote>
          </div>
          <div className='about-right'>
            <p>Anna Hovan is a contemporary artist born and based in Armenia. Her creative path began with fashion design and gradually evolved into fine art. She developed her artistic language through sustained self-directed practice, deepened by academic studio training and specialized art programs. Her work explores the female image as a reflection of identity, capturing the interplay between outward appearance and the inner life, and revealing the depth and complexity of being.</p>
            <p>Anna Hovan explores identity as a living process of transformation, a state in which a person exists between external image and inner essence, between the visible and the inexpressible. Contemporary culture offers ready-made templates for self-definition: through fashion, social codes, and standards of beauty. Yet these forms often become shells that fail to reflect the depth of human experience. In her work, Hovan moves beyond visual conventions, transforming the human figure into a space for reflection on who we remain when social and aesthetic masks are removed. For her, beauty is not an aesthetic criterion or an ideal, but a living energy of presence, an expression of inner harmony that can be fragile and contradictory. She is drawn to the moment when the external image ceases to be mere form and becomes a reflection of inner state, when a person briefly coincides with their true self. The figures in her paintings are not portraits but metaphors of human existence. They inhabit the boundary between the material and the metaphysical, the physical and the spiritual. Through the interplay of color, space, and rhythm, Hovan creates a visual language in which identity is revealed not as a fixed result, but as movement, fluid, unstable, and alive. Her paintings are both an act of self-exploration and an invitation to inner dialogue. The viewer does not simply observe the figure; they recognize within it their own experience of being.</p>
          </div>
        </div>
      </section>
    </>
  );
}

export default Main;
